import { FC, useEffect, useState } from "react";
import { t } from "i18next";

import { InputWithCornerHint } from "@/shared/components/atoms/InputWithCornerHint";
import TableListCompanies from "@/shared/components/organisms/TableListCompanies";
import AlertErrorButtons from "@/shared/components/molecules/AlertErrorButtons";
import { DialogCreate } from "@/shared/components/molecules/DialogCreate";
import AlertSuccess from "@/shared/components/molecules/AlertSuccess";
import AlertError from "@/shared/components/molecules/AlertError";
import { InputFile } from "@/shared/components/atoms/InputFile";
import { TextArea } from "@/shared/components/atoms/TextArea";
import { toasts } from "@/shared/utils/toasts";

import { QueryMetaData } from "@/app/gyms/gyms";
import {
  useCreateGymMutation,
  useDeleteGymMutation,
  useFetchGymsMutation,
  useUpdateGymMutation,
} from "@/app/gyms/gyms.api";
import { DOMAIN } from "@/app/app.api";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDebounce } from "@/shared/hooks/debounce";
import { Permission, usePermission } from "@/shared/hooks/usePermission";

const initialValues = {
  gym: "",
  //profile_avatar: `${DOMAIN}/uploads/gyms/default.png`, // Para la imagen
  profile_avatar: "",
  benefits: "",
  link_privacy: "",
  link_terms: "",
  link_site: "",
};

const gymValidationSchema = Yup.object({
  gym: Yup.string().required(t("gym.views.input_mandatory")),
  profile_avatar: Yup.mixed().required(t("gym.views.input_mandatory")),
  link_privacy: Yup.string()
    .url(t("gym.views.invalid_field"))
    .required(t("gym.views.input_mandatory")),
  link_terms: Yup.string()
    .url(t("gym.views.invalid_field"))
    .required(t("gym.views.input_mandatory")),
  link_site: Yup.string()
    .matches(
      /^www\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/,
      t("gym.views.invalid_field")
    )
    .required(t("gym.views.input_mandatory")),
  benefits: Yup.string().optional().nullable(),
});

type EditFormData = {
  gym: string;
  profile_avatar: string | File;
  benefits: string;
  link_privacy: string;
  link_terms: string;
  link_site: string;
};

export const Companies: FC<any> = () => {
  // Mutación para obtener gyms
  const [fetchGyms, { isError, isLoading }] = useFetchGymsMutation();
  //Mutación para crear gyms
  const [createGym] = useCreateGymMutation();
  //Mutación para editar gyms
  const [updateGym, { isLoading: isLoadingUpdateGym }] = useUpdateGymMutation();
  // Estado para setear formato de las compañías
  const [companies, setCompanies] = useState([]);
  //Estado para guardar toda la respuesta de gyms
  const [companiesFull, setCompaniesFull] = useState([]);
  // Mutación para eliminar gyms
  const [deleteGym, { isLoading: isLoadingDeleteGym }] = useDeleteGymMutation();
  //Controlar Carga de acciones
  const [loadingActions, setLoadingLoadingActions] = useState(false);
  // Controlar Abre/Cierre Dialog para crear
  const [open, setOpen] = useState(false);
  // Controlar Abre/Cierre del Alert para confirmación de eliminar
  const [openDelete, setOpenDelete] = useState(false);
  // Estado para el ID del gimnasio a eliminar
  const [gymToDelete, setGymToDelete] = useState<number | null>(null);
  //State para abrir cerrar el AlertSuccess
  const [openSuccess, setOpenSuccess] = useState(false);
  //State para guardar el mensaje del AlertSuccess
  const [successMessage, setSuccessMessage] = useState("");
  //State para abrir cerrar el AlertError
  const [openError, setOpenError] = useState(false);

  const [isLoadingRE, setIsLoadingRE] = useState(false);

  //State para capturar lo que el usuario escribe en la barra de busqueda
  const [search, setSearch] = useState<string>("");
  const debounceSearch = useDebounce(search, 500);

  //State para abrir cerrar el dialog de Editar Gym
  const [openEdit, setOpenEdit] = useState(false);
  //State para capturar el id a editar
  const [idEdit, setIdEdit] = useState<string | number | null>(null);

  // Estado para el formulario de edición (mantenemos el estado anterior para el modal de edición)
  const [editFormData, setEditFormData] = useState<EditFormData | null>(null);

  const [page, setPage] = useState(1);
  const [entries, setEntries] = useState(50);
  const [queryMetaData, setQueryMetaData] = useState<QueryMetaData>({
    page: 1,
    perPage: 1,
    total: 1,
    lastPage: 1,
  });

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: gymValidationSchema,
    onSubmit: async (values) => {
      const formToSend = new FormData();
      formToSend.append("gym", values.gym);
      formToSend.append("benefits", values.benefits);
      formToSend.append("link_privacy", values.link_privacy);
      formToSend.append("link_terms", values.link_terms);
      formToSend.append("link_site", values.link_site);

      if (values.profile_avatar) {
        formToSend.append("profile_avatar", values.profile_avatar);
      }

      try {
        setIsLoadingRE(true);
        setLoadingLoadingActions(true);
        await createGym(formToSend).unwrap();
        setOpen(false);
        formik.resetForm();
        fetchGymsData();

        toasts.success(
          "Compañía registrada",
          t("gym.controller.successfull_add")
        );

        setIsLoadingRE(false);
        setLoadingLoadingActions(false);
      } catch (error) {
        toasts.danger(
          "Error al agregar la compañía",
          "Por favor, inténtelo de nuevo más tarde."
        );
        setIsLoadingRE(false);
      }
    },
  });

  const formikEdit = useFormik({
    initialValues: editFormData,
    validationSchema: gymValidationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const formToSend = new FormData();
      formToSend.append("gym", values.gym);
      formToSend.append("benefits", values.benefits);
      formToSend.append("link_privacy", values.link_privacy);
      formToSend.append("link_terms", values.link_terms);
      formToSend.append("link_site", values.link_site);

      if (values.profile_avatar) {
        formToSend.append("profile_avatar", values.profile_avatar);
      }

      try {
        setLoadingLoadingActions(true);
        await updateGym({ gymId: idEdit, gymData: formToSend }).unwrap();
        setOpenEdit(false);
        setEditFormData(null);

        toasts.success(
          "Compañía actualizada",
          t("gym.controller.successfull_edit")
        );

        await fetchGymsData();
        setLoadingLoadingActions(false);
      } catch (error) {
        toasts.danger(
          "Error al editar la compañía",
          "Por favor, inténtelo de nuevo más tarde."
        );
        setIsLoadingRE(false);
      }
    },
  });

  // Función para manejar cambios de página
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleEntriesChange = (entries: number) => {
    setEntries(entries);
  };

  // Función para abrir/cerrar el AlertErrorButtons
  const handleOpenDelete = (id: number | null) => {
    setGymToDelete(id); // Establecemos el gimnasio a eliminar
    setOpenDelete(!openDelete); // Abrimos el AlertError
  };

  // Función para eliminar gyms
  const onDelete = async () => {
    if (gymToDelete !== null) {
      try {
        setLoadingLoadingActions(true);
        await deleteGym(gymToDelete).unwrap();
        await fetchGymsData();
        toasts.success(
          "Compañía eliminada",
          t("gym.controller.successfull_delete")
        );
        if (debounceSearch) setSearch(""); //clear search after delete gym
      } catch (error) {
        console.error("Error deleting gym:", error);
        toasts.danger(
          "Error al eliminar la compañía",
          "Oops! No pudimos eliminar la compañía, por favor, inténtelo de nuevo más tarde."
        );
      } finally {
        setLoadingLoadingActions(false);
        handleOpenDelete(null);
      }
    }
  };
  //USE EFFECT TO FETCH DATA CUANDO SE CARGA EL COMPONENTE
  const handleOpenEdit = (id: string | number | null) => {
    setIdEdit(id);
    const companyEdit = companiesFull.find((company) => company.id === id);
    if (companyEdit) {
      setEditFormData({
        gym: companyEdit.gym,
        profile_avatar: companyEdit?.logo,
        benefits: companyEdit.benefits,
        link_privacy: companyEdit.link_privacy,
        link_terms: companyEdit.link_terms,
        link_site: companyEdit.link_site,
      });
      setOpenEdit(true);
    } else {
      console.error("No se encontró la compañía con ID:", id);
    }
  };

  //Traer datos de gyms
  const fetchGymsData = async () => {
    try {
      const response = await fetchGyms({
        search: debounceSearch,
        page: debounceSearch ? 1 : page,
        limit: entries,
      }).unwrap();
      const arrayCompanies = [];
      let objetCompanies = {};
      setQueryMetaData({
        page: response.current_page,
        perPage: response.per_page,
        total: response.total,
        lastPage: response.last_page,
      });
      setEntries(response.per_page);
      for (let i = 0; i < response.data.length; i++) {
        objetCompanies = {
          id: response.data[i].id,
          name: response.data[i].gym,
          image: response.data[i].logo,
          branchs: response.data[i].branchs_count,
          users: response.data[i].users_count,
          status: response.data[i].status,
        };
        arrayCompanies.push(objetCompanies);
      }
      setCompaniesFull(response.data);
      setCompanies(arrayCompanies);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchGymsData();
  }, [fetchGyms, debounceSearch, page, entries]);

  const { can } = usePermission();

  return (
    <div className="flex flex-col items-center h-full w-full mt-8">
      <div className="w-full sm:max-w-full md:max-w-full lg:max-w-full xl:max-w-full 2xl:max-w-7xl mx-auto mb-8">
        <TableListCompanies
          companies={companies}
          handlePageChange={handlePageChange}
          handleEntriesChange={handleEntriesChange}
          entries={entries}
          queryMetaData={queryMetaData}
          title={t("gym.views.gyms")}
          description={t("gym.views.list_gyms")}
          onClickTop={() => {
            setOpen(true);
          }}
          isLoading={isLoading}
          onDelete={handleOpenDelete}
          onEdit={handleOpenEdit}
          searchQuery={search}
          onSearchChange={(e) => setSearch(e.target.value)}
          loadingActions={loadingActions}
          canCreateEditCompany={can(Permission.COMPANY_CREATE_AND_EDIT)}
          canDeleteCompany={can(Permission.COMPANY_DELETE)}
        />
        <AlertSuccess
          open={openSuccess}
          onClose={() => setOpenSuccess(false)}
          title={t("gym.views.action_complete")}
          message={successMessage}
          buttonText={t("general.accept")}
        />
        <AlertError
          open={openError}
          onClose={() => setOpenError(false)}
          title={t("general.error")}
          message={t("gym.javascript.validation")}
          buttonText={t("general.accept")}
        />
        <AlertErrorButtons
          open={openDelete}
          onClose={() => handleOpenDelete(null)}
          title={t("gym.views.delete_confirmation_title")}
          message={t("gym.views.delete_confirmation_message")}
          primaryButtonText={t("general.delete")}
          secondaryButtonText={t("general.cancel")}
          onPrimaryButtonClick={onDelete}
          onSecondaryButtonClick={() => handleOpenDelete(null)}
          isLoading={isLoadingDeleteGym}
        />
        {/* DIALOG CREATE GYM */}
        <DialogCreate
          OnclickCreate={formik.handleSubmit}
          OnclickCancel={() => {
            setOpen(false);
            formik.resetForm();
          }}
          onClose={() => {
            setOpen(false);
            formik.resetForm();
          }}
          tittleButtonCreate={t("gym.views.create")}
          tittleButtonCancel={t("gym.views.cancel")}
          open={open}
          setOpen={setOpen}
          tittle={t("gym.views.new")}
          subTittle={t("gym.views.create_gyms")}
          isLoading={isLoadingRE}
        >
          <div className="mt-6 mx-2">
            <div className="space-y-12">
              <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                <div className="flex items-center justify-center h-full text-center">
                  <div>
                    <h2 className="text-base font-semibold leading-7 text-gray-900">
                      {t("gym.views.profile")}
                    </h2>
                    <p className="mt-1 text-sm leading-6 text-gray-600">
                      {t("gym.views.profile_description")}
                    </p>
                  </div>
                </div>
                <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                  <div className="col-span-full">
                    <InputWithCornerHint
                      label={t("gym.views.name")}
                      span={t("general.mandatory")}
                      id="gym"
                      name="gym"
                      type="text"
                      placeholder={t("gym.views.name_camera")}
                      value={formik.values.gym}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.gym && Boolean(formik.errors.gym)}
                      textError={formik.touched.gym && formik.errors.gym}
                    />
                  </div>
                  <div className="col-span-full">
                    <InputFile
                      name="profile_avatar"
                      id="profile_avatar"
                      label={t("gym.views.logo")}
                      span={t("general.mandatory")}
                      spanButton={t("gym.views.upload_image")}
                      description={t("gym.views.format_image")}
                      selectedImage={formik.values.profile_avatar}
                      onFileChange={(file) => {
                        formik.setFieldValue("profile_avatar", file);
                      }}
                      onRemoveImage={() => {
                        formik.setFieldValue("profile_avatar", "");
                      }}
                      tittleDelete={t("gym.views.delete_image")}
                    />
                    {formik?.errors?.profile_avatar &&
                      formik?.touched?.profile_avatar && (
                        <span className="text-red-500 text-sm mt-2">
                          {formik.errors.profile_avatar}
                        </span>
                      )}
                  </div>
                  <div className="col-span-full">
                    <TextArea
                      htmlFor="benefits"
                      label={t("gym.views.benefis")}
                      spanMessage={t("general.optional")}
                      id="benefits"
                      name="benefits"
                      value={formik.values.benefits}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3">
                <div className="flex items-center justify-center h-full text-center">
                  <div>
                    <h2 className="text-base font-semibold leading-7 text-gray-900">
                      {t("gym.views.link")}
                    </h2>
                    <p className="mt-1 text-sm leading-6 text-gray-600">
                      {t("gym.views.link_description")}
                    </p>
                  </div>
                </div>
                <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                  <div className="col-span-full">
                    <InputWithCornerHint
                      label={t("gym.views.privacy")}
                      span={t("general.mandatory")}
                      id="link_privacy"
                      name="link_privacy"
                      type="url"
                      placeholder="http://privacy.com"
                      value={formik.values.link_privacy}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.link_privacy &&
                        Boolean(formik.errors.link_privacy)
                      }
                      textError={
                        formik.touched.link_privacy &&
                        formik.errors.link_privacy
                      }
                    />
                  </div>
                  <div className="col-span-full">
                    <InputWithCornerHint
                      label={t("gym.views.terms")}
                      span={t("general.mandatory")}
                      id="link_terms"
                      name="link_terms"
                      type="url"
                      placeholder="http://terms.com"
                      value={formik.values.link_terms}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.link_terms &&
                        Boolean(formik.errors.link_terms)
                      }
                      textError={
                        formik.touched.link_terms && formik.errors.link_terms
                      }
                    />
                  </div>
                  <div className="col-span-full">
                    <InputWithCornerHint
                      label={t("gym.views.site")}
                      span={t("general.mandatory")}
                      id="link_site"
                      name="link_site"
                      type="url"
                      placeholder="http://site.com"
                      value={formik.values.link_site}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.link_site &&
                        Boolean(formik.errors.link_site)
                      }
                      textError={
                        formik.touched.link_site && formik.errors.link_site
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogCreate>

        {/* DIALOG EDIT GYM */}
        <DialogCreate
          OnclickCreate={formikEdit.handleSubmit}
          OnclickCancel={() => {
            setOpenEdit(false);
            setEditFormData(null);
            formikEdit.resetForm();
          }}
          isLoading={isLoadingUpdateGym}
          acceptDisabled={isLoadingUpdateGym}
          tittleButtonCreate={t("gym.views.edit")}
          tittleButtonCancel={t("gym.views.cancel")}
          open={openEdit}
          setOpen={() => {
            setOpenEdit(false);
          }}
          onClose={() => {
            formikEdit.resetForm();
          }}
          tittle={t("gym.views.new")}
          subTittle={t("gym.views.edit_gyms")}
        >
          <div className="mt-6 mx-2">
            <div className="space-y-12">
              <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                <div className="flex items-center justify-center h-full text-center">
                  <div>
                    <h2 className="text-base font-semibold leading-7 text-gray-900">
                      {t("gym.views.profile")}
                    </h2>
                    <p className="mt-1 text-sm leading-6 text-gray-600">
                      {t("gym.views.profile_description")}
                    </p>
                  </div>
                </div>
                <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                  <div className="col-span-full">
                    <InputWithCornerHint
                      label={t("gym.views.name")}
                      span={t("general.mandatory")}
                      id="gym"
                      name="gym"
                      type="text"
                      placeholder={t("gym.views.name_camera")}
                      value={formikEdit.values?.gym}
                      onChange={formikEdit.handleChange}
                      error={
                        formikEdit?.touched?.gym &&
                        Boolean(formikEdit?.errors?.gym)
                      }
                      textError={t("gym.views.input_mandatory")}
                    />
                  </div>
                  <div className="col-span-full">
                    <InputFile
                      name="profile_avatar"
                      id="profile_avatar"
                      label={t("gym.views.logo")}
                      span={t("general.mandatory")}
                      spanButton={t("gym.views.upload_image")}
                      description={t("gym.views.format_image")}
                      selectedImage={`${DOMAIN}/uploads/gyms/${editFormData?.profile_avatar}`}
                      onFileChange={(file) => {
                        /* setEditFormData({
                          ...editFormData,
                          profile_avatar: file,
                        }); */
                        formikEdit.setFieldValue("profile_avatar", file);
                      }}
                      onRemoveImage={() =>
                        /* setEditFormData({
                          ...editFormData,
                          profile_avatar: "",
                        }); */
                        formikEdit.setFieldValue("profile_avatar", "")
                      }
                      tittleDelete={t("gym.views.delete_image")}
                    />
                  </div>
                  <div className="col-span-full">
                    <TextArea
                      htmlFor="benefits"
                      label={t("gym.views.benefis")}
                      spanMessage={t("general.optional")}
                      id="benefits"
                      name="benefits"
                      value={formikEdit?.values?.benefits || ""}
                      onChange={formikEdit.handleChange}
                    />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3">
                <div className="flex items-center justify-center h-full text-center">
                  <div>
                    <h2 className="text-base font-semibold leading-7 text-gray-900">
                      {t("gym.views.link")}
                    </h2>
                    <p className="mt-1 text-sm leading-6 text-gray-600">
                      {t("gym.views.link_description")}
                    </p>
                  </div>
                </div>
                <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                  <div className="col-span-full">
                    <InputWithCornerHint
                      label={t("gym.views.privacy")}
                      span={t("general.mandatory")}
                      id="link_privacy"
                      name="link_privacy"
                      type="url"
                      placeholder="http://privacy.com"
                      value={formikEdit?.values?.link_privacy}
                      onChange={formikEdit.handleChange}
                      error={
                        formikEdit?.touched?.link_privacy &&
                        Boolean(formikEdit?.errors?.link_privacy)
                      }
                      textError={t("gym.views.input_mandatory")}
                    />
                  </div>
                  <div className="col-span-full">
                    <InputWithCornerHint
                      label={t("gym.views.terms")}
                      span={t("general.mandatory")}
                      id="link_terms"
                      name="link_terms"
                      type="url"
                      placeholder="http://terms.com"
                      value={formikEdit?.values?.link_terms}
                      onChange={formikEdit.handleChange}
                      error={
                        formikEdit?.touched?.link_terms &&
                        Boolean(formikEdit?.errors?.link_terms)
                      }
                      textError={t("gym.views.input_mandatory")}
                    />
                  </div>
                  <div className="col-span-full">
                    <InputWithCornerHint
                      label={t("gym.views.site")}
                      span={t("general.mandatory")}
                      id="link_site"
                      name="link_site"
                      type="url"
                      placeholder="http://site.com"
                      value={formikEdit?.values?.link_site}
                      onChange={formikEdit.handleChange}
                      error={
                        formikEdit?.touched?.link_site &&
                        formikEdit?.errors?.link_site
                      }
                      textError={t("gym.views.input_mandatory")}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogCreate>
      </div>
    </div>
  );
};
